// ─────────────────────────────────────────────────────────────────────
// "I have a setup code" — Round 17 (employees WITHOUT an e-mail address)
//
// IT issues an 8-digit code on Admin → Access Management → Account Security
// (Get code). The employee enters Emp ID + code + a new password here; the
// code is single use, expires (Auth:SetupCodeMinutes), and only works with
// the Emp ID it was issued for. Public page; the API rate-limits attempts.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Container, Row, Input, Label, Button, Alert, Spinner } from "reactstrap";
import api from "../../api";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

export default function SetupCode() {
    const [policy, setPolicy] = useState({ minLength: 12, maxLength: 128 });
    const [help, setHelp] = useState({ itHelpText: "", setupCodeMinutes: 60 });
    const [empId, setEmpId] = useState("");
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState([]);
    const [done, setDone] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("Auth/password-policy").then((r) => { if (alive && r?.data?.minLength) setPolicy(r.data); }).catch(() => {});
        api.get("Auth/help-info").then((r) => { if (alive && r?.data) setHelp(r.data); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setErrors([]);
        if (!empId.trim()) return setError("Enter your Emp ID.");
        if (code.replace(/[\s-]/g, "").length !== 8) return setError("The setup code has 8 digits.");
        if (password.length < policy.minLength) return setError(`The password must be at least ${policy.minLength} characters.`);
        if (password !== confirm) return setError("The password and its confirmation do not match.");
        setBusy(true);
        try {
            await api.post("Auth/redeem-setup-code", { empId: empId.trim(), code: code.replace(/[\s-]/g, ""), newPassword: password });
            setDone(true); setPassword(""); setConfirm(""); setCode("");
        } catch (err) {
            const d = err?.response?.data;
            setError((d && (d.error || d.message)) || "Could not set the password.");
            if (Array.isArray(d?.errors)) setErrors(d.errors);
        } finally { setBusy(false); }
    };

    return (
        <ParticlesAuth>
            <div className="auth-page-content">
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card className="mt-4">
                                <CardBody className="p-4">
                                    <div className="text-center mt-2">
                                        <h5 className="text-primary">I have a setup code</h5>
                                        <p className="text-muted">
                                            For employees without an e-mail address. Enter the code the IT team gave you
                                            (valid {help.setupCodeMinutes} minutes, single use) and choose your password.
                                            You will sign in with your Emp ID and this password.
                                        </p>
                                    </div>
                                    {done ? (
                                        <Alert color="success" className="text-center">
                                            Your password is set. <Link to="/login" className="fw-semibold">Sign in</Link> with your Emp ID.
                                        </Alert>
                                    ) : (
                                        <form onSubmit={submit} autoComplete="off" className="p-2">
                                            {error && (
                                                <Alert color="danger">
                                                    {error}
                                                    {errors.length > 1 && <ul className="mb-0 mt-1">{errors.map((x, i) => <li key={i}>{x}</li>)}</ul>}
                                                </Alert>
                                            )}
                                            <div className="mb-3">
                                                <Label htmlFor="sc-emp">Emp ID</Label>
                                                <Input id="sc-emp" value={empId} onChange={(e) => setEmpId(e.target.value)} disabled={busy} autoComplete="username" />
                                            </div>
                                            <div className="mb-3">
                                                <Label htmlFor="sc-code">Setup code (8 digits)</Label>
                                                <Input id="sc-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} disabled={busy}
                                                       placeholder="e.g. 4821 0937" style={{ letterSpacing: 2, fontFamily: "monospace" }} />
                                            </div>
                                            <div className="mb-3">
                                                <Label htmlFor="sc-pw">New password</Label>
                                                <Input id="sc-pw" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                                                       disabled={busy} autoComplete="new-password" maxLength={policy.maxLength} />
                                                <div className="form-text">At least {policy.minLength} characters — a short phrase works well.</div>
                                            </div>
                                            <div className="mb-3">
                                                <Label htmlFor="sc-pw2">Confirm password</Label>
                                                <Input id="sc-pw2" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                                       disabled={busy} autoComplete="new-password" maxLength={policy.maxLength} />
                                            </div>
                                            <div className="form-check mb-3">
                                                <Input type="checkbox" className="form-check-input" id="sc-show" checked={show} onChange={(e) => setShow(e.target.checked)} />
                                                <Label className="form-check-label" htmlFor="sc-show">Show passwords</Label>
                                            </div>
                                            <Button color="success" className="w-100" type="submit" disabled={busy}>
                                                {busy ? <><Spinner size="sm" /> Setting…</> : "Set my password"}
                                            </Button>
                                        </form>
                                    )}
                                    {help.itHelpText && (
                                        <p className="text-muted small text-center mt-3 mb-0">Need a code? {help.itHelpText}</p>
                                    )}
                                </CardBody>
                            </Card>
                            <div className="mt-4 text-center">
                                <p className="mb-0">Back to <Link to="/login" className="fw-semibold text-primary text-decoration-underline">Sign in</Link></p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </ParticlesAuth>
    );
}
